import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalculateIcon from '@mui/icons-material/Calculate';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import LogoutIcon from '@mui/icons-material/Logout';
import SchoolIcon from '@mui/icons-material/School';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { useAuth } from '../contexts/AuthContext';
import { companiesApi, Company } from '../services/companies';

const drawerWidth = 240;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Clients', icon: <PeopleIcon />, path: '/clients' },
  { text: 'CRM - Leads', icon: <ContactMailIcon />, path: '/leads' },
  { text: 'Timesheets', icon: <AccessTimeIcon />, path: '/timesheets' },
  { text: 'Approvals', icon: <CheckCircleIcon />, path: '/approvals' },
  { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices' },
  { text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
  { text: 'Expenses', icon: <ShoppingCartIcon />, path: '/expenses' },
  { text: 'Bank', icon: <AccountBalanceIcon />, path: '/bank' },
  { text: 'Payroll', icon: <AttachMoneyIcon />, path: '/payroll' },
  { text: 'Contributions', icon: <CalculateIcon />, path: '/contributions' },
  { text: 'Accounting', icon: <AssessmentIcon />, path: '/accounting' },
  { text: 'Tax Config', icon: <SettingsIcon />, path: '/tax-config' },
  { text: 'Training & Objectives', icon: <SchoolIcon />, path: '/training-objectives' },
  { text: 'Company Config', icon: <BusinessIcon />, path: '/company-configuration' },
  { text: 'User Management', icon: <GroupIcon />, path: '/user-management' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const loadCompany = async () => {
      console.log('Layout: Loading company, user:', user);
      if (user?.companyId) {
        try {
          console.log('Layout: Fetching company with ID:', user.companyId);
          const companyData = await companiesApi.getById(user.companyId);
          console.log('Layout: Company data loaded:', companyData);
          setCompany(companyData);
        } catch (error: any) {
          console.error('Failed to load company:', error);
          console.error('Error details:', error.response?.data);
        }
      } else {
        console.warn('Layout: No companyId found in user object');
      }
    };
    loadCompany();
    
    // Listen for company update events
    const handleCompanyUpdate = () => {
      console.log('Layout: Company update event received, reloading...');
      loadCompany();
    };
    window.addEventListener('companyUpdated', handleCompanyUpdate);
    
    return () => {
      window.removeEventListener('companyUpdated', handleCompanyUpdate);
    };
  }, [user?.companyId]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Barefoot SBA
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {company ? (
            <Box display="flex" alignItems="center" gap={1.5} sx={{ mr: 2 }}>
              {company.logoUrl ? (
                <Box
                  component="img"
                  src={company.logoUrl.startsWith('http') 
                    ? company.logoUrl 
                    : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${company.logoUrl}`}
                  alt={company.name}
                  sx={{
                    width: 56,
                    height: 56,
                    objectFit: 'contain',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onError={(e) => {
                    console.error('Failed to load logo image:', company.logoUrl);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <Avatar
                  sx={{ width: 56, height: 56 }}
                  variant="rounded"
                >
                  {company.name.charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Typography variant="h6" noWrap component="div" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {company.name}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ mr: 2, color: 'warning.main' }}>
              Loading company...
            </Typography>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find((item) => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.name}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;

